/**
 * Thin fetch helper used by every app. 401 carries a `loginUrl` for redirect;
 * 503 means "auth service unavailable, try again". Network failures (offline,
 * CORS preflight) surface as ApiError with status 0 so the UI doesn't have to
 * branch on TypeError.
 *
 * Naming convention at the API boundary: list endpoints return raw snake_case
 * DB rows; curated detail/view objects use camelCase. Mix is intentional and
 * documented per-app in its own types file.
 */
export class ApiError extends Error {
  constructor(public status: number, public payload: unknown) {
    super(`API error ${status}`)
  }
  get loginUrl(): string | null {
    if (this.status !== 401) return null
    const p = this.payload as { loginUrl?: unknown } | null
    return typeof p?.loginUrl === "string" ? p.loginUrl : null
  }
  get serverMessage(): string | null {
    const p = this.payload as { error?: unknown } | null
    return typeof p?.error === "string" ? p.error : null
  }
}

export type QueryValue = string | number | string[] | undefined | null

export interface ApiInit extends RequestInit {
  /** Convenience: serializes to a JSON body + sets content-type. Mutually exclusive with `body`. */
  json?: unknown
  /** Convenience: serializes into query string. Repeats array values. */
  query?: Record<string, QueryValue>
  /**
   * Override the URL base used to resolve `path`. Defaults to
   * `window.location.origin` so same-origin calls "just work" in an SPA.
   * Provide an absolute URL string here for cross-origin clients.
   */
  baseUrl?: string
}

export async function api<T = unknown>(path: string, init: ApiInit = {}): Promise<T> {
  const base = init.baseUrl ?? (typeof window !== "undefined" ? window.location.origin : "http://localhost")
  const url = new URL(path, base)
  if (init.query) {
    for (const [k, v] of Object.entries(init.query)) {
      if (v === undefined || v === null) continue
      if (Array.isArray(v)) for (const x of v) url.searchParams.append(k, String(x))
      else url.searchParams.set(k, String(v))
    }
  }
  const headers = new Headers(init.headers)
  let body: BodyInit | null | undefined = init.body
  if (init.json !== undefined) {
    if (init.body !== undefined) throw new Error("api(): pass either `json` or `body`, not both")
    headers.set("content-type", "application/json")
    body = JSON.stringify(init.json)
  }
  let res: Response
  try {
    res = await fetch(url.toString(), {
      ...init,
      headers,
      body,
      // Default to include for same-origin cookie auth; explicit init.credentials wins.
      credentials: init.credentials ?? "include",
    })
  } catch (err) {
    throw new ApiError(0, { error: networkErrorMessage(err) })
  }
  const text = await res.text()
  const data = text ? safeJsonParse(text) : null
  if (!res.ok) throw new ApiError(res.status, data)
  return data as T
}

function safeJsonParse(s: string): unknown {
  try { return JSON.parse(s) } catch { return s }
}

function networkErrorMessage(err: unknown): string {
  if (err instanceof TypeError) return "Network error — check your connection and retry"
  return err instanceof Error ? err.message : "Unknown network error"
}
